import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LifeBuoy, LockKeyhole, LogOut, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Loading, PrimaryButtonClass } from "@/components/common/Chrome";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LINE = {
  pending: "This tenant has not been enabled yet. The licensing server has to switch the "
         + "subscription on before the console can be used.",
  suspended: "The licensing server has suspended this subscription. Everything is safely held; "
           + "nothing has been deleted.",
  expired: "This subscription has passed its end date and is waiting to be renewed.",
};

const Blocked = ({ state }) => {
  const { logout } = useAuth();
  return (
    <div className="grid min-h-screen place-items-center bg-background px-6"
         data-testid="licence-blocked">
      <div className="panel w-full max-w-[560px] p-7 text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-amber-500/12 text-amber-600 dark:text-amber-400">
          <LockKeyhole className="h-5 w-5" />
        </span>
        <h1 className="mt-4 font-display text-[22px] font-bold tracking-tight text-foreground">
          Subscription {state.status}
        </h1>
        <p className="mx-auto mt-2 max-w-[430px] text-[12.5px] text-muted-foreground"
           data-testid="licence-blocked-line">
          {LINE[state.status] || LINE.pending}
        </p>
        <div className="mt-3 font-mono text-[11px] text-muted-foreground">
          {state.tenant_id} · {state.licence_key}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {state.can_read_subscription && (
            <Link to="/settings/subscription">
              <Button size="sm" className={`h-9 text-[12px] ${PrimaryButtonClass}`}
                      data-testid="licence-blocked-subscription">
                <LifeBuoy className="mr-1.5 h-3.5 w-3.5" /> Open Subscription
              </Button>
            </Link>
          )}
          <Button size="sm" variant="outline" className="h-9 rounded-full text-[12px]"
                  onClick={() => window.location.reload()} data-testid="licence-blocked-recheck">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Check again
          </Button>
          <Button size="sm" variant="ghost" className="h-9 text-[12px]" onClick={logout}
                  data-testid="licence-blocked-signout">
            <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
};

export const LicenceGate = ({ children }) => {
  const [state, setState] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/licence/status")
      .then((r) => setState(r.data))
      .catch(() => setState({ status: "active" }));
  }, []);

  if (!state) {
    return <div className="grid min-h-screen place-items-center"><Loading label="Checking your licence" /></div>;
  }
  const blocked = state.status !== "active";
  if (!blocked) return children(false);
  if (location.pathname === "/settings/subscription" && state.can_read_subscription) {
    return children(true);
  }
  return <Blocked state={state} />;
};
